import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  machinesService,
  type CreateMachineInput,
  type UpdateMachineInput,
} from "../../services/admin/machines.service";

export function useMachinesAdmin() {
  return useQuery({
    queryKey: ["machines-admin"],
    queryFn: () => machinesService.getAll(),
  });
}

export function useActiveMachines() {
  return useQuery({
    queryKey: ["active-machines"],
    queryFn: () => machinesService.getActive(),
    staleTime: 60_000,
  });
}

export function useCreateMachine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateMachineInput) => machinesService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["machines-admin"] });
      queryClient.invalidateQueries({ queryKey: ["machines"] });
      queryClient.invalidateQueries({ queryKey: ["online-machines"] });
    },
  });
}

export function useUpdateMachine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateMachineInput) => machinesService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["machines-admin"] });
      queryClient.invalidateQueries({ queryKey: ["machines"] });
      queryClient.invalidateQueries({ queryKey: ["online-machines"] });
    },
  });
}

export function useToggleMachineActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      machinesService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["machines-admin"] });
      queryClient.invalidateQueries({ queryKey: ["machines"] });
      queryClient.invalidateQueries({ queryKey: ["online-machines"] });
    },
  });
}

export function useSetMachineOnlineStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isOnline }: { id: string; isOnline: boolean }) =>
      machinesService.setOnlineStatus(id, isOnline),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["machines-admin"] });
      queryClient.invalidateQueries({ queryKey: ["machines"] });
      queryClient.invalidateQueries({ queryKey: ["online-machines"] });
    },
  });
}
